import type { HTMLAttributes, ReactNode, Ref } from 'react'

import { cn } from '@gto/ui/cn'

import { linearProgressVariants } from './linear-progress.styles'

type LinearProgressColor =
  | 'primary'
  | 'primary-contrast'
  | 'secondary'
  | 'inherit'
  | 'info'
  | 'error'
  | 'warning'

type LinearProgressSize = 'sm' | 'md' | 'lg'

/**
 * Determinate fills to a fixed percentage; indeterminate animates an
 * unbounded wait.
 *
 * `buffer` from the Figma component (a dotted secondary track) is intentionally
 * not yet ported — it is rarely used. Add it here when a consumer needs it.
 */
type LinearProgressType = 'determinate' | 'indeterminate'

type LinearProgressBaseProps = Omit<HTMLAttributes<HTMLDivElement>, 'role' | 'color'> & {
  /** Bar colour from the design system. Defaults to `primary`. */
  color?: LinearProgressColor
  /** Track height: `sm` (4px, default hairline), `md` (8px), or `lg` (12px). */
  size?: LinearProgressSize
  /** Custom label node beside the bar. Takes precedence over `showValueLabel`. */
  label?: ReactNode
  className?: string
  /** Per-slot class overrides for cases the `color` variant can't express (e.g. a data-driven bar colour). */
  classNames?: { track?: string; bar?: string }
  ref?: Ref<HTMLDivElement>
  'data-tst'?: string
}

/**
 * `type` discriminates the two shapes:
 * - `determinate` (the default) requires `value` and may auto-render a
 *   `{value}%` label via `showValueLabel`.
 * - `indeterminate` has no progress value, so `value` and `showValueLabel`
 *   are not representable on it.
 */
type LinearProgressProps =
  | (LinearProgressBaseProps & {
      type?: 'determinate'
      /** Progress percentage 0–100. Clamped to that range before rendering. */
      value: number
      /** When true, renders the auto-computed `{value}%` label beside the bar. */
      showValueLabel?: boolean
    })
  | (LinearProgressBaseProps & {
      type: 'indeterminate'
    })

const clampPercent = (value: number): number => Math.min(100, Math.max(0, value))

const LinearProgress = (props: LinearProgressProps) => {
  const isDeterminate = props.type !== 'indeterminate'

  // Normalise the union so every component-owned key is present, then
  // destructure all of them out — `domProps` is left with only DOM attributes.
  const {
    color = 'primary',
    size = 'sm',
    type,
    value,
    showValueLabel,
    label,
    className,
    classNames,
    ref,
    'data-tst': dataTestId,
    ...domProps
  } = isDeterminate ? props : { ...props, value: undefined, showValueLabel: undefined }

  const clampedValue = isDeterminate && value != null ? clampPercent(value) : undefined

  const labelContent =
    label ?? (showValueLabel === true && clampedValue != null ? `${clampedValue}%` : null)

  const styles = linearProgressVariants({ color, size, type })

  return (
    <div
      ref={ref}
      className={cn(styles.root(), className)}
      data-tst={dataTestId}
      data-ui="linear-progress"
      {...domProps}
    >
      <div
        aria-valuemax={isDeterminate ? 100 : undefined}
        aria-valuemin={isDeterminate ? 0 : undefined}
        aria-valuenow={clampedValue}
        className={cn(styles.track(), classNames?.track)}
        role="progressbar"
      >
        <div
          style={isDeterminate ? { width: `${clampedValue ?? 0}%` } : undefined}
          className={cn(
            styles.bar(),
            !isDeterminate && 'animate-linear-progress-indeterminate-1',
            classNames?.bar,
          )}
        />
        {!isDeterminate && (
          <div
            className={cn(styles.bar(), 'animate-linear-progress-indeterminate-2', classNames?.bar)}
          />
        )}
      </div>
      {labelContent != null && <span className={styles.label()}>{labelContent}</span>}
    </div>
  )
}

export { LinearProgress }
export type { LinearProgressColor, LinearProgressProps, LinearProgressSize, LinearProgressType }
